
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, User, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import FadeInWhenVisible from "./FadeInWhenVisible";
import { supabase } from "@/integrations/supabase/client";
import { PlatformStatisticsService } from "@/services/statistics-service";
import { PlatformStatistics } from "@/services/statistics/types";

const CtaSection: React.FC = () => {
  const [stats, setStats] = useState<PlatformStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlatformStats = async () => {
      try {
        const platformStats = await PlatformStatisticsService.getPlatformStatistics();
        setStats(platformStats);
      } catch (error) {
        console.error("Error fetching platform statistics:", error);
        // Fallback to default values on error
        setStats({
          totalDownloads: "1M+",
          activeUsers: "50K+",
          averageRating: 4.8
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPlatformStats();
  }, []);

  // Default placeholder values while loading
  const statsData = stats || {
    totalDownloads: "1M+",
    activeUsers: "50K+",
    averageRating: 4.8
  };

  return (
    <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <FadeInWhenVisible>
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                Ready to transform your workflow with AI?
              </h2>
              <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
                Join thousands of developers and companies using AI agents to automate tasks and enhance productivity
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-purple-700 hover:bg-purple-100 px-8 shadow-lg transition-all duration-300 hover:shadow-xl" asChild>
                  <Link to="/signup">Get Started for Free</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white text-purple-700 hover:bg-purple-100 px-8 shadow-lg transition-all duration-300 hover:shadow-xl"
                  asChild
                >
                  <Link to="/browse?tab=featured">Explore Featured Agents</Link>
                </Button>
              </div>
            </div>
          </FadeInWhenVisible>

          <FadeInWhenVisible delay={0.2}>
            <div className="mt-12 bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <p className="text-lg font-medium mb-4">Join our growing community</p>
              <div className="flex flex-wrap justify-center gap-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center mr-2">
                    <Download className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">{statsData.totalDownloads}</div>
                    <div className="text-sm text-purple-200">Downloads</div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center mr-2">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">{statsData.activeUsers}</div>
                    <div className="text-sm text-purple-200">Active Users</div>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center mr-2">
                    <Star className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">{statsData.averageRating}</div>
                    <div className="text-sm text-purple-200">Average Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInWhenVisible>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
